import { createFileRoute, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import Loader from "@/components/core/Loaders/Loader";
import { servicesOrchestratorValues } from "@/config/services";
import { requireAuth } from "@/lib/guards";
import { pricingSearchSchema } from "@/lib/search-params";
import { platformApi } from "@/services";
import { useStore } from "@/store";
import { logError } from "@/utils/logging/error";
import logger from "@/utils/logging/logger";

export const Route = createFileRoute("/$locale/$partner/pricing")({
	beforeLoad: requireAuth,
	component: QuickPricing,
	validateSearch: (search) => pricingSearchSchema.parse(search),
});

// Mirrors the message contract pricing posts to its host iframe; only the fields read here are typed.
type PricingMessage =
	| { type: "price"; eligible: boolean }
	| { type: "error"; error: string }
	| { type: "exit" }
	| { type: "manual-identification"; sessionId: string };

function QuickPricing() {
	const navigate = useNavigate();
	const { locale, partner } = useParams({ from: "/$locale/$partner" });
	const { manualIdentification } = useSearch({ from: "/$locale/$partner/pricing" });
	const locationId = useStore((state) => state.location?.id);
	const partnerCode = useStore((state) => state.location?.Partner?.code);
	const category = useStore((state) => state.category);
	const setAttribute = useStore((state) => state.setAttribute);
	const setDeviceInfo = useStore((state) => state.setDeviceInfo);
	const setSessionId = useStore((state) => state.setSessionId);
	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		setAttribute("manualIdentification", manualIdentification);
	}, [manualIdentification, setAttribute]);

	const quickPricingUrl = useMemo(() => {
		const devicePath = manualIdentification ? `/${category.toLowerCase()}` : "";
		const extraParam = manualIdentification ? "manualIdentification=true" : "quickPricing=true";
		return `${servicesOrchestratorValues.urls.pricing}/${locale}/${partnerCode}${devicePath}?iframe=true&skipOfferAction=true&locationId=${locationId}&${extraParam}`;
	}, [locale, partnerCode, manualIdentification, category, locationId]);

	const navigateHome = useCallback(() => {
		navigate({ to: "/$locale/$partner/home", params: { locale, partner }, replace: true });
	}, [navigate, locale, partner]);

	const handleManualIdentification = useCallback(
		async (sessionId: string) => {
			try {
				const session = await platformApi.getSessionById(sessionId);
				const rawMeta = session.Identification?.metadata;
				const device = rawMeta
					? ((typeof rawMeta === "string" ? JSON.parse(rawMeta) : rawMeta) as Record<string, unknown>)
					: null;

				setDeviceInfo({
					imei: session.Identification?.identifier ?? undefined,
					brand: device?.brand as string | undefined,
					model: device?.model as string | undefined,
					description: device?.description as string | undefined,
					capacity: device?.capacity as string | undefined,
					category,
				});

				setSessionId(sessionId);
				navigate({ to: "/$locale/$partner/conditions", params: { locale, partner }, replace: true });
			} catch (err) {
				logError(err, "Failed to perform manual identification");
				navigateHome();
			}
		},
		[locale, partner, navigate, navigateHome, category, setDeviceInfo, setSessionId],
	);

	// Every outcome except the mid-flow manual identification ends the embedded pricing session.
	const handleMessageEvent = useCallback(
		async (event: MessageEvent<PricingMessage>) => {
			const message = event.data;

			switch (message?.type) {
				case "price":
					logger.info("INFO", "Pricing finished", { eligible: message.eligible });
					navigateHome();
					break;
				case "error":
					logger.error("FLOW", "Pricing threw error", { reason: String(message.error).slice(0, 200) });
					navigateHome();
					break;
				case "exit":
					logger.info("INFO", "Pricing closed by the user");
					navigateHome();
					break;
				case "manual-identification":
					if (manualIdentification) await handleManualIdentification(message.sessionId);
					break;
			}
		},
		[navigateHome, manualIdentification, handleManualIdentification],
	);

	useEffect(() => {
		if (!partnerCode) {
			logger.error("FLOW", "Pricing Flow parameter is undefined or empty");
			navigate({ to: "/$locale/$partner/home", params: { locale, partner }, replace: true });
			return;
		}

		window.addEventListener("message", handleMessageEvent);

		return () => {
			window.removeEventListener("message", handleMessageEvent);
		};
	}, [handleMessageEvent, partnerCode, navigate, locale, partner]);

	return (
		<div className="h-[calc(100vh-5rem)]">
			<iframe
				id="iframe"
				title="Pricing App"
				onLoad={() => {
					setIsLoading(false);
				}}
				src={quickPricingUrl}
				height="100%"
				width="100%"
			/>
			{isLoading && (
				<div className="absolute w-fit h-fit top-1/3 left-1/2">
					<Loader />
				</div>
			)}
		</div>
	);
}
